import {fitCamera} from './hits.mjs';
import {NEAR_ZOOM} from './follow.mjs';
import {pinScale} from './pins.mjs';

// Query coordinates are separate from lat/lon: those always describe the shared camera.
export function readSearchOrigin(params) {
  const source = params.get('source');
  const lat = Number(params.get('query_lat')), lon = Number(params.get('query_lon'));
  const valid = params.has('query_lat') && params.has('query_lon') &&
    params.get('query_lat').trim() && params.get('query_lon').trim() &&
    Number.isFinite(lat) && Number.isFinite(lon) && lat >= 47 && lat <= 55.2 && lon >= 5.5 && lon <= 15.6;
  const label = source === 'address' && valid ? params.get('query_label')?.trim() || null : null;
  return {source, label, point: valid && (source === 'gps' || source === 'address') ? [lon, lat] : null,
    autoGPS: source === 'gps'};
}

export function writeSearchOrigin(params, origin) {
  if (origin.source !== null) params.set('source', origin.source);
  params.delete('query_label');
  if (origin.source === 'address' && origin.point && origin.label) params.set('query_label', origin.label);
  if (origin.point) {
    params.set('query_lat', origin.point[1]); params.set('query_lon', origin.point[0]);
  }
}

// Only explicit MCP position mode may locate without a click; query links wait for framing.
export function initialLocate(origin, {hasHits}) {
  return origin.autoGPS && !origin.point && !hasHits ? 'follow' : null;
}

// Normal/legacy and address links never inspect permission or start location, even with an existing grant.
export async function fitSearchOrigin(map, coords, origin, locator, padding, camera, isCurrent = () => true, move = true) {
  const all = origin.point ? [...coords, origin.point] : coords;
  if (!all.length) return;
  const view = fitCamera(map, all, null, padding(), NEAR_ZOOM);
  if (view && move) map.jumpTo(camera(view));
  if (isCurrent() && origin.autoGPS && locator.state === 'off') {
    locator.follow(c => fitCamera(map, coords.length ? coords : all, [c.longitude, c.latitude], padding(), NEAR_ZOOM)?.zoom ?? null);
  }
}

// Built like a station pin: same head/tip geometry and scale, a darker core (dimColor 44) holding the glyph
// where a station pin holds its count. Its label is outside the anchored box.
export function searchOriginMarker(map, maplibre, origin, label) {
  if (origin.source !== 'address' || !origin.point) return null;
  const element = document.createElement('div'), scale = pinScale();
  element.className = 'search-origin'; element.setAttribute('role', 'img');
  element.dataset.i18nAria = 'searchLocation';
  element.setAttribute('aria-label', label);
  if (origin.label) element.setAttribute('aria-description', origin.label);
  // Only constant geometry is markup; URL/user text is always textContent, never HTML.
  element.innerHTML = '<svg viewBox="28 25 40 52" aria-hidden="true"><path class="search-origin-pin" d="M48 76 L33.3 55.6 A18.1 18.1 0 1 1 62.7 55.6 Z"/><circle class="search-origin-core" cx="48" cy="45" r="14.8"/><g class="search-origin-aim"><path d="M48 35.5v4.5m0 10v4.5M38.5 45h4.5m10 0h4.5"/><path d="M48 44v2"/></g></svg>';
  const text = document.createElement('span'); text.className = 'search-origin-label';
  text.textContent = origin.label || label; text.title = origin.label || label;
  if (!origin.label) { text.dataset.i18n = 'searchLocation'; text.dataset.i18nTitle = 'searchLocation'; }
  element.append(text);
  const marker = new maplibre.Marker({element, anchor: 'bottom', offset: [0, scale]})
    .setLngLat(origin.point).addTo(map);
  // Keep the text inside the map viewport when the query pin is near an edge.
  const placeLabel = () => {
    const x = map.project(origin.point).x, width = map.getContainer().clientWidth;
    const half = text.getBoundingClientRect().width / 2;
    const center = x < 0 || x > width ? x : Math.max(half + 12, Math.min(width - half - 12, x));
    text.style.marginLeft = `${center - x}px`;
  };
  map.on('move', placeLabel); map.on('resize', placeLabel); placeLabel();
  const remove = marker.remove?.bind(marker);
  marker.remove = () => { map.off?.('move', placeLabel); map.off?.('resize', placeLabel); remove?.(); };
  return marker;
}
